import { useQuery } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import api from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";

  const { data, isLoading } = useQuery({
    queryKey: ["search", q],
    queryFn: () => api.get("/search", { params: { q } }).then((r) => r.data),
    enabled: !!q,
  });

  const results = data || { articles: [], herbs: [], therapies: [] };
  const total = results.articles.length + results.herbs.length + results.therapies.length;

  return (
    <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-12">
      <div className="flex items-center gap-2 text-brand-clay">
        <SearchIcon className="h-5 w-5" />
        <span className="text-xs font-semibold uppercase tracking-widest">Search</span>
      </div>
      <h1 className="mt-3 font-heading text-4xl text-brand-ink tracking-tight">
        Results for “{q}”
      </h1>
      {!isLoading && <p className="mt-2 text-brand-muted">{total} result{total === 1 ? "" : "s"} across articles, herbs and therapies.</p>}

      {isLoading ? (
        <p className="mt-16 text-center text-brand-muted">Searching…</p>
      ) : (
        <div className="mt-10 space-y-12">
          {results.articles.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl text-brand-ink">Articles</h2>
              <div className="mt-5 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
                {results.articles.map((a, i) => (
                  <ArticleCard key={a.slug} article={a} index={i} />
                ))}
              </div>
            </section>
          )}
          {results.herbs.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl text-brand-ink">Herbs</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {results.herbs.map((h) => (
                  <Link key={h.slug} to={`/herbs/${h.slug}`} className="flex gap-4 rounded-xl border border-brand-line bg-white p-4 hover:border-brand">
                    <img src={h.image} alt="" className="h-16 w-16 rounded-lg object-cover" />
                    <div>
                      <h3 className="font-heading text-lg text-brand-ink">{h.name}</h3>
                      <p className="line-clamp-2 text-sm text-brand-muted">{h.summary}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
          {results.therapies.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl text-brand-ink">Therapies</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {results.therapies.map((t) => (
                  <Link key={t.slug} to={`/therapies/${t.slug}`} className="flex gap-4 rounded-xl border border-brand-line bg-white p-4 hover:border-brand">
                    <img src={t.image} alt="" className="h-16 w-16 rounded-lg object-cover" />
                    <div>
                      <h3 className="font-heading text-lg text-brand-ink">{t.name}</h3>
                      <p className="line-clamp-2 text-sm text-brand-muted">{t.summary}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
          {total === 0 && q && (
            <p className="text-center text-brand-muted">
              Nothing found. Try a different term or <Link to="/articles" className="text-brand underline">browse articles</Link>.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
